/**
 * SettingsCard — right-rail toggles for all session boolean flags.
 * Mirrors `.settings-card` from the prototype (cms-redesign-mockup.html
 * ~8655–8699 + CSS lines 4976–5012), extended with a collapsed
 * "Advanced flags" sub-section so every toggle lives in one place
 * instead of being split between the rail and the main form.
 *
 *   <SettingsCard formData={formData} onChange={(name, v) => …} />
 *
 * Layout:
 *   1. Primary toggles (the 5 the prototype models in the rail)
 *      — Active / Recommended / Free / New / Dark mode
 *   2. Sub-heading "Advanced flags" with a 1px sand divider above
 *   3. Advanced toggles — No avatar / Is chapter / Show rating /
 *      Skip caption generation. These are less-frequently flipped
 *      flags that don't earn space in the publishing summary but
 *      still belong with the other session settings.
 *
 * Each row is the prototype's `.toggle-row.compact` recipe:
 *   • label-c — 13/600 navy
 *   • desc    — 10.5 text-muted, contains the mono field name so
 *               admins know which DB column they're flipping
 *   • toggle  — pill, scale(0.92), sand → teal-green when on
 *
 * Bound to the same `formData` shape used everywhere else in the
 * V2 form — onChange just calls `setField(name, 0|1)`.
 */
import React from 'react';
import RailCard, { RailCardHead } from '../../redesign/edit/RailCard';

const PRIMARY_ROWS = [
  { name: 'session_is_active',      label: 'Active' },
  { name: 'session_is_recommended', label: 'Recommended' },
  { name: 'session_is_free',        label: 'Free access' },
  { name: 'session_is_new',         label: 'Mark as new' },
  { name: 'session_is_dark_mode',   label: 'Dark mode display' },
];

const ADVANCED_ROWS = [
  { name: 'no_avatar',              label: 'No avatar',              hint: 'Hide avatar on the player' },
  { name: 'session_is_chapter',     label: 'Is chapter',             hint: 'Audiobook chapter' },
  { name: 'session_allow_rating',   label: 'Show rating',            hint: 'Enable in-session rating prompt' },
  { name: 'session_skip_captions',  label: 'Skip caption generation', hint: 'Bypass the captions pipeline for this session' },
];

// Render a pill toggle at the prototype's compact scale (0.92).
// Inlined here rather than reusing ToggleSwitch because the
// compact variant doesn't have a side label — the label-c sits in
// the row at full size while the pill itself shrinks slightly.
function CompactToggle({ checked, onChange, name }) {
  return (
    <label
      style={{
        position: 'relative',
        width: 36,
        height: 20,
        flexShrink: 0,
        cursor: 'pointer',
        display: 'inline-block',
        transform: 'scale(0.92)',
      }}
    >
      <input
        type="checkbox"
        name={name}
        checked={!!checked}
        onChange={onChange}
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      />
      <span
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: 999,
          background: checked ? '#75A293' /* teal-green */ : '#E8E0CF' /* sand */,
          transition: 'background 160ms ease',
        }}
      />
      <span
        aria-hidden
        style={{
          position: 'absolute',
          top: 2,
          left: checked ? 18 : 2,
          width: 16,
          height: 16,
          borderRadius: '50%',
          background: '#FFFFFF',
          boxShadow: '0 1px 3px #2C3E5040',
          transition: 'left 160ms ease',
        }}
      />
    </label>
  );
}

/* Single toggle row — used for both primary and advanced lists. */
function ToggleRow({ row, value, onChange, isLast }) {
  return (
    <div
      className="flex items-center justify-between"
      style={{
        padding: '10px 0',
        borderBottom: isLast ? 'none' : '1px solid #ECE3D2' /* line */,
        gap: 12,
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="text-navy" style={{ fontSize: 13, fontWeight: 600 }}>
          {row.label}
        </div>
        <div
          className="text-text-muted"
          style={{ marginTop: 1, fontSize: 10.5 }}
        >
          <span className="font-mono">{row.name}</span>
          {row.hint && (
            <span style={{ marginLeft: 6, fontStyle: 'italic' }}>
              · {row.hint}
            </span>
          )}
        </div>
      </div>
      <CompactToggle
        name={row.name}
        checked={!!value}
        onChange={onChange}
      />
    </div>
  );
}

export default function SettingsCard({ formData, onChange }) {
  const allRows = [...PRIMARY_ROWS, ...ADVANCED_ROWS];
  const enabledCount = allRows.reduce(
    (n, r) => n + (formData[r.name] ? 1 : 0),
    0,
  );

  return (
    <RailCard
      variant="settings"
      head={({ open, toggle }) => (
        <RailCardHead
          title="Settings"
          variant="settings"
          open={open}
          onToggle={toggle}
          pill={
            <span
              className="font-mono text-text-muted bg-cream border border-sand"
              style={{
                fontSize: 11,
                padding: '2px 8px',
                borderRadius: 999,
                whiteSpace: 'nowrap',
              }}
            >
              {enabledCount}/{allRows.length}
            </span>
          }
        />
      )}
    >
      <div style={{ padding: '6px 18px 14px' }}>
        {/* Primary toggles — the 5 documented in the prototype */}
        {PRIMARY_ROWS.map((row, i) => (
          <ToggleRow
            key={row.name}
            row={row}
            value={formData[row.name]}
            onChange={(e) => onChange(row.name, e.target.checked ? 1 : 0)}
            isLast={i === PRIMARY_ROWS.length - 1}
          />
        ))}

        {/* Sub-section heading — matches the FormSection eyebrow
            recipe (Asap Condensed 10.5 / 700 / muted, 0.1em tracking)
            so it reads as a divider rather than a separate card. */}
        <div
          className="font-condensed font-bold uppercase text-text-muted"
          style={{
            fontSize: 10.5,
            letterSpacing: '0.1em',
            marginTop: 14,
            paddingTop: 12,
            borderTop: '1px solid #E8E0CF', /* sand */
            marginBottom: 2,
          }}
        >
          Advanced flags
        </div>

        {/* Advanced toggles — kept here so all session booleans live
            in one place, but visually deprioritized via the eyebrow
            heading + lighter spacing above. */}
        {ADVANCED_ROWS.map((row, i) => (
          <ToggleRow
            key={row.name}
            row={row}
            value={formData[row.name]}
            onChange={(e) => onChange(row.name, e.target.checked ? 1 : 0)}
            isLast={i === ADVANCED_ROWS.length - 1}
          />
        ))}
      </div>
    </RailCard>
  );
}
